<template>
  <div>
    <v-row align="center">
      <v-col cols="12" md="3">
        <v-text-field
          v-model="startDate"
          label="Start Date"
          type="date"
          clearable
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="endDate"
          label="End Date"
          type="date"
          clearable
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedEvent"
          :items="eventOptions"
          label="Scanning Event"
        ></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-btn @click="handleUpdate" color="primary" :loading="loading" :disabled="!isDirty" id="update-data-btn">Update</v-btn>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <div class="position-relative">
      <v-overlay :model-value="loading" class="align-center justify-center" persistent>
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </v-overlay>

      <v-row :class="{ 'loading-content': loading }">
        <v-col cols="12" md="6">
          <PieChart title="SDK Versions" :series="sdkVersionsData.series" :chart-labels="sdkVersionsData.labels" />
        </v-col>
        <v-col cols="12" md="6">
          <TotalScansLineChart title="Total Scans" :series="totalScansData.series" :categories="totalScansData.categories" />
        </v-col>
      </v-row>
    </div>

    <v-divider class="my-4"></v-divider>

    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Top 20 Scanning Apps</v-card-title>
          <v-data-table
            :headers="appsHeaders"
            :items="topApps"
            item-key="name"
          >
            <template v-slot:[`item.scans`]="{ item }">
              {{ item.scans.toLocaleString() }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Top 20 Developers</v-card-title>
          <v-data-table
            :headers="developersHeaders"
            :items="topDevelopers"
            item-key="name"
          >
            <template v-slot:[`item.scans`]= "{ item }">
              {{ item.scans.toLocaleString() }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="my-4"></v-divider>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Subscriber Report</span>
            <v-btn small color="secondary" @click="copySubscriberList">Copy List</v-btn>
          </v-card-title>
          <v-expansion-panels>
            <v-expansion-panel
              v-for="tier in subscriberReport"
              :key="tier.tier_name"
            >
              <v-expansion-panel-title>
                {{ tier.tier_name }} ({{ tier.subscriber_count }} subscribers)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-list dense>
                  <v-list-item
                    v-for="email in tier.subscribers"
                    :key="email"
                  >
                    <v-list-item-title>{{ email }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn color="blue" variant="text" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import { ref, onMounted, computed } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';
import PieChart from '@/components/charts/PieChart.vue';
import TotalScansLineChart from '@/components/charts/TotalScansLineChart.vue';

export default {
  name: 'EcosystemReport',
  components: {
    PieChart,
    TotalScansLineChart,
  },
  setup() {
    const { getAccessTokenSilently } = useAuth0();

    const loading = ref(false);
    const startDate = ref(null);
    const endDate = ref(null);
    const selectedEvent = ref('Both');
    const eventOptions = ['AccessToMesh Only', 'MeshExtract Only', 'Both'];

    const lastRequestState = ref({});

    const isDirty = computed(() => {
      return (
        startDate.value !== lastRequestState.value.startDate ||
        endDate.value !== lastRequestState.value.endDate ||
        selectedEvent.value !== lastRequestState.value.selectedEvent
      );
    });

    const sdkVersionsData = ref({ series: [], labels: [] });
    const totalScansData = ref({ series: [], categories: [] });
    const topApps = ref([]);
    const topDevelopers = ref([]);

    const subscriberReport = ref([]);
    const snackbar = ref(false);
    const snackbarText = ref('');

    const fetchSdkVersions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const params = {};
        if (startDate.value) params.start_date = startDate.value;
        if (endDate.value) params.end_date = endDate.value;

        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/ecosystem/sdk`, { headers, params });

        const data = response.data;

        let otherCount = 0;
        const mainData = [];

        data.forEach(item => {
          if (item.distinct_app_count < 5) {
            otherCount += item.distinct_app_count;
          } else {
            mainData.push(item);
          }
        });

        if (otherCount > 0) {
          mainData.push({ sdk_version: 'Other', distinct_app_count: otherCount });
        }

        const series = mainData.map(item => item.distinct_app_count);
        const labels = mainData.map(item => item.sdk_version);

        sdkVersionsData.value = { series, labels };

      } catch (error) {
        console.error('Error fetching SDK versions:', error);
      }
    };

    const fetchTotalScans = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const params = {};
        if (startDate.value) params.start_date = startDate.value;
        if (endDate.value) params.end_date = endDate.value;

        let eventTypeApiValue = 'both';
        if (selectedEvent.value === 'AccessToMesh Only') {
          eventTypeApiValue = 'accesstomesh';
        } else if (selectedEvent.value === 'MeshExtract Only') {
          eventTypeApiValue = 'meshextract';
        }
        params.event_type = eventTypeApiValue;

        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/ecosystem/scans`, { headers, params });

        const data = response.data;
        const seriesData = data.map(item => item.total_events_count);
        const categories = data.map(item => new Date(item.week_start_date).toLocaleDateString());

        totalScansData.value = { series: [{ name: 'Total Scans', data: seriesData }], categories };

      } catch (error) {
        console.error('Error fetching total scans:', error);
      }
    };

    const fetchTopApps = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const params = {};
        if (startDate.value) params.start_date = startDate.value;
        if (endDate.value) params.end_date = endDate.value;

        let eventTypeApiValue = 'both';
        if (selectedEvent.value === 'AccessToMesh Only') {
          eventTypeApiValue = 'accesstomesh';
        } else if (selectedEvent.value === 'MeshExtract Only') {
          eventTypeApiValue = 'meshextract';
        }
        params.event_type = eventTypeApiValue;

        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/ecosystem/apps`, { headers, params });

        topApps.value = response.data.map(item => ({
          name: item.app_name,
          scans: item.total_scan_events,
        }));

      } catch (error) {
        console.error('Error fetching top apps:', error);
      }
    };

    const fetchTopDevelopers = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const params = {};
        if (startDate.value) params.start_date = startDate.value;
        if (endDate.value) params.end_date = endDate.value;

        let eventTypeApiValue = 'both';
        if (selectedEvent.value === 'AccessToMesh Only') {
          eventTypeApiValue = 'accesstomesh';
        } else if (selectedEvent.value === 'MeshExtract Only') {
          eventTypeApiValue = 'meshextract';
        }
        params.event_type = eventTypeApiValue;

        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/ecosystem/developers`, { headers, params });

        topDevelopers.value = response.data.map(item => ({
          name: item.developer_name,
          scans: item.total_scan_events, // Assuming total_scan_events from API
        }));

      } catch (error) {
        console.error('Error fetching top developers:', error);
      }
    };

    const fetchSubscriberReport = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/reports/ecosystem/subscribers`, { headers });
        subscriberReport.value = response.data;
      } catch (error) {
        console.error('Error fetching subscriber report:', error);
      }
    };

    const copySubscriberList = () => {
      if (!subscriberReport.value) return;

      let textToCopy = '';
      for (const tier of subscriberReport.value) {
        textToCopy += `${tier.tier_name}\n`;
        textToCopy += `${tier.subscribers.join('\n')}\n\n`;
      }
      
      navigator.clipboard.writeText(textToCopy.trim()).then(() => {
        snackbarText.value = 'Subscriber list copied to clipboard!';
        snackbar.value = true;
      }).catch(err => {
        snackbarText.value = 'Failed to copy text.';
        snackbar.value = true;
        console.error('Failed to copy:', err);
      });
    };

    const fetchData = async () => {
      loading.value = true;
      await Promise.all([fetchSdkVersions(), fetchTotalScans(), fetchTopApps(), fetchTopDevelopers(), fetchSubscriberReport()]);
      loading.value = false;

      lastRequestState.value = {
        startDate: startDate.value,
        endDate: endDate.value,
        selectedEvent: selectedEvent.value,
      };
    };

    onMounted(() => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      startDate.value = firstDay.toISOString().split('T')[0];
      endDate.value = lastDay.toISOString().split('T')[0];

      fetchData();
    });

    const handleUpdate = () => {
      fetchData();
    };

    // Table headers
    const appsHeaders = [
      { title: 'App Name', key: 'name' },
      { title: 'Scans', key: 'scans' },
    ];
    const developersHeaders = [
      { title: 'Developer', key: 'name' },
      { title: 'Scans', key: 'scans' },
    ];
    // topDevelopers ref is already defined as ref([])

    return {
      loading,
      startDate,
      endDate,
      selectedEvent,
      eventOptions,
      appsHeaders,
      topApps,
      developersHeaders,
      topDevelopers,
      sdkVersionsData,
      totalScansData,
      handleUpdate,
      isDirty,
      subscriberReport,
      copySubscriberList,
      snackbar,
      snackbarText,
    };
  },
};
</script>

<style scoped>
.position-relative {
  position: relative;
}
.loading-content {
  opacity: 0.5;
  pointer-events: none;
}
</style>